
import VueApp from '@/@types/app/VueApp';
import { WorkspaceViewArticleItem } from '@/@types/WorkspaceViewArticleItem';
import { WorkspaceOrder } from '@/@types/WorkspaceOrder';

import EventBus from '@/EventBus';
import { ARTICLE_CREATE_HIDE, ARTICLE_CREATE_UPDATE_ARTICLE } from '@/events';
import { Article } from '@/models/article/Article';
import { TextTab } from '@/models/article/TextTab';
import { Workspace } from '@/models/Workspace';
import articleEditService from '@/services/article/ArticleEditService';
import authService from '@/services/AuthService';
import notificationService from '@/services/NotificationService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import { commitSetArticleEditorArticle } from '@/store/commits/articleEditorCommits';
import { dispatchGetDraftsCount } from '@/store/dispatchers/uiDispatchers';
import { Component, Watch } from 'vue-property-decorator';

@Component({
  name: 'SubArticleCreateDialog.vue',
})
export default class SubArticleCreate extends VueApp {
  parentArticle: WorkspaceViewArticleItem = null;
  parentGroup: WorkspaceOrder = null;
  parentType: 'article' | 'group' = 'article';
  parentWorkspace: Workspace = null;
  subArticle: Article = null;
  showDialog = false;
  resolve: ((value?: Article | PromiseLike<Article>) => void) | null = null;
  reject: ((reason?: any) => void) | null = null;
  initValidate = false;
  searchQuery = '';
  searchedWorkspaces: Workspace[] = [];

  $refs: {
    tagInput: any;
  };

  get workspaces(): Workspace[] {
    return this.$store.state.workspaces;
  }

  get parentTitle(): string {
    if (this.parentType === 'group') {
      return this.parentGroup?.title || '';
    }
    return this.parentArticle?.title || '';
  }

  handleParentWorkspaceTitle(workspace: Workspace): string {
    return workspaceService.getWorkspaceTitle(workspace);
  }

  checkChips() {
    const inputValue = this.$refs.tagInput.inputValue.trim();
    if (inputValue.length > 0) {
      this.subArticle.tags.push(inputValue);
      this.$refs.tagInput.inputValue = '';
    }
  }

  initSubArticle(parentArticle: WorkspaceViewArticleItem) {
    this.subArticle.workspace = workspaceService.workspaceView.workspace.id;
    this.subArticle.parentArticle = parentArticle.id;
  }

  initSubArticleForGroup(parentGroup: WorkspaceOrder) {
    this.subArticle.workspace = workspaceService.workspaceView.workspace.id;
    this.subArticle.parentArticle = parentGroup.value; // Store group ID in parentArticle field
  }

  show(articleItem: WorkspaceViewArticleItem, workspace: Workspace) {
    this.parentType = 'article';
    this.parentArticle = articleItem;
    this.parentGroup = null;
    this.subArticle = new Article();
    this.parentWorkspace = workspace;
    this.searchQuery = this.handleParentWorkspaceTitle(workspace);

    this.initSubArticle(this.parentArticle);
    return new Promise<Article>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      this.showDialog = true;
    });
  }

  showForGroup(groupNode: WorkspaceOrder, workspace: Workspace) {
    this.parentType = 'group';
    this.parentGroup = groupNode;
    this.parentArticle = null;
    this.subArticle = new Article();
    this.parentWorkspace = workspace;
    this.searchQuery = this.handleParentWorkspaceTitle(workspace);

    this.initSubArticleForGroup(this.parentGroup);
    return new Promise<Article>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      this.showDialog = true;
    });
  }

  isValid() {
    return (
      !this.initValidate || (this.parentType === 'group' ? this.parentGroup?.title : this.parentArticle?.['title'])
    );
  }

  validate() {
    this.initValidate = true;
    return this.isValid();
  }

  async saveTextArticle() {
    this.initValidate = true;
    if (this.isValid()) {
      if (!this.subArticle.title.trim().length) {
        notificationService.warning('Title cannot be empty!');
        return;
      }
      this.checkChips();

      this.subArticle.textTabs = [new TextTab()];

      commitSetArticleEditorArticle(this.subArticle);

      const userId = authService.user.id;
      const res = await articleEditService.save({ updatedByUser: userId, isPublish: false, skipEditor: true });
      this.subArticle.id = res.data.article.id;

      this.resolve(this.subArticle);
      this.showDialog = false;

      await dispatchGetDraftsCount();
    }
  }

  hideCreateArticle() {
    this.showDialog = false;
    this.subArticle = null;
    this.parentArticle = null;
    this.parentGroup = null;
    this.parentWorkspace = null;
  }

  mounted() {
    EventBus.$on(ARTICLE_CREATE_UPDATE_ARTICLE, () => {
      commitSetArticleEditorArticle(this.subArticle);
    });

    EventBus.$on(ARTICLE_CREATE_HIDE, this.hideCreateArticle);
  }

  beforeDestroy() {
    EventBus.$off(ARTICLE_CREATE_UPDATE_ARTICLE);
    EventBus.$off(ARTICLE_CREATE_HIDE);
  }

  @Watch('showDialog')
  onDialogClose(value: boolean) {
    if (!value) {
      this.initValidate = false;
    }
  }
}
